import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { AppThread, ApplicationItem } from '@pathonline/shared';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { getJson, sendJson } from '../api.ts';
import { hubDateTime, plural } from '../format.ts';
import './applications.css';

const enc = encodeURIComponent;
const pretty = (s: string) => s.replace(/_/g, ' ');

/** /my-applications and /inbox: a list of cards; the selected thread opens over it. */
export function ApplicationsPage({ mode, selected }: { mode: 'mine' | 'inbox'; selected?: string }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['applications', mode], queryFn: () => getJson<ApplicationItem[]>(`/api/applications/${mode}`) });
  const select = (app?: string) => void navigate({ to: mode === 'mine' ? '/my-applications' : '/inbox', search: app ? { app } : {} });
  // The inbox is grouped by topic, in the order the topics first come.
  const groups: { label: string; apps: ApplicationItem[] }[] = [];
  for (const a of q.data ?? []) {
    const key = mode === 'inbox' ? a.label : '';
    const g = groups.find((x) => x.label === key);
    if (g) g.apps.push(a);
    else groups.push({ label: key, apps: [a] });
  }
  return (
    <div className="ap-wrap">
      <h1 className="ap-h">
        <span aria-hidden="true">{mode === 'mine' ? '📄' : '📥'}</span> {mode === 'mine' ? 'My applications' : 'Applications inbox'}
      </h1>
      <p className="page-sub">
        {mode === 'mine' ? (
          <>Your applications and the private conversation with each supervisor. <Link to="/apply">Browse open topics →</Link></>
        ) : (
          'Applications for the topics you lead or supervise. Only you, the applicant, managers and administrators see a thread.'
        )}
      </p>
      {q.isPending && <p className="empty">Loading…</p>}
      {q.isError && <p className="empty error">{q.error.message}</p>}
      {q.data?.length === 0 && <p className="empty">{mode === 'mine' ? 'You have not applied yet.' : 'No applications yet.'}</p>}
      <div className="ap-list">
        {groups.map((g) => (
          <Fragment key={g.label}>
            {g.label && <h2 className="ap-grp">{g.label}</h2>}
            {g.apps.map((a) => (
              <button key={a.appId} type="button" className={`ap-item ${selected === a.appId ? 'is-selected' : ''}`} onClick={() => select(a.appId)}>
                <span>
                  <span className="ap-title">
                    {mode === 'inbox' ? (
                      <>
                        {a.applicant}
                        {a.applicantType && <span className="ap-type"> · {a.applicantType}</span>}
                      </>
                    ) : (
                      a.label
                    )}
                  </span>
                  <span className="ap-meta">{plural(a.msgCount, 'message')} · updated {hubDateTime(a.updated)}</span>
                </span>
                <span className={`app-st st-${a.status}`}>{pretty(a.status)}</span>
              </button>
            ))}
          </Fragment>
        ))}
      </div>
      {/* key: a new thread starts with an empty reply box and nothing armed */}
      {selected && <Thread key={selected} appId={selected} onClose={() => select(undefined)} />}
    </div>
  );
}

export function Thread({ appId, onClose }: { appId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['applications', 'thread', appId], queryFn: () => getJson<AppThread>(`/api/applications/${enc(appId)}`) });
  const [text, setText] = useState('');
  const [armed, setArmed] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  /** The status picked in the select, '' = none yet. Only Update saves it: each save emails the applicant. */
  const [pick, setPick] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['applications'] });
  /** Runs one save; false when it failed (the message says why). */
  const act = async (f: () => Promise<unknown>, ok: string): Promise<boolean> => {
    setBusy(true);
    try {
      await f();
      setMsg(ok);
      await refresh();
      return true;
    } catch (e) {
      setMsg((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const box = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  /** The press started on the backdrop (a drag out of the box is not a click on it). */
  const down = useRef(false);
  const titleId = useId();
  // A reload that fails keeps the thread, and the reply being typed: the error is said above it.
  const t = q.data;
  const status = pick || t?.app.status || '';
  // Escape and a click beside the box keep a typed reply or an unsaved status: only × closes then.
  const close = () => !text.trim() && (!pick || pick === t?.app.status) && onClose();
  // Focus moves into the thread, and back to the card when it closes.
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    return () => back?.focus();
  }, []);
  // A modal: Escape closes it, Tab stays inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab' || !box.current) return;
      const all = [...box.current.querySelectorAll<HTMLElement>('button:enabled, select:enabled, textarea:enabled')];
      const i = all.indexOf(document.activeElement as HTMLElement);
      // Wrap at either end; from outside the box (the page behind) come back in.
      // Inside the box but not in the list (the scrolling messages): the browser moves on.
      const out = !box.current.contains(document.activeElement);
      if (e.shiftKey ? out || i === 0 : out || i === all.length - 1) {
        e.preventDefault();
        all[e.shiftKey ? all.length - 1 : 0]?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const canWithdraw = !!t && t.isOwner && t.app.status !== 'withdrawn';
  return (
    <div
      className="modal-back"
      role="presentation"
      onMouseDown={(e) => (down.current = e.target === e.currentTarget)}
      onClick={(e) => down.current && e.target === e.currentTarget && close()}
    >
      <div ref={box} className="th-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        {/* The header and × show in every state: loading, error, loaded. */}
        <div className="th-hd">
          <div>
            <h2 className="th-title" id={titleId}>{t ? t.app.label : 'Application thread'}</h2>
            {t && (
              <p className="th-sub">
                {t.app.applicant}{t.app.applicantType ? ` · ${t.app.applicantType}` : ''} · {pretty(t.app.status)}
              </p>
            )}
          </div>
          <button ref={closeButton} type="button" className="th-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {q.isPending && <p className="th-load">Loading…</p>}
        {q.isError && <p className="th-err">{q.error.message}</p>}
        {t && (
          <>
            <div className="th-msgs">
              {t.messages.length === 0 && <p className="th-empty">No messages yet.</p>}
              {t.messages.map((m, i) => (
                <div key={i} className={`th-m ${m.mine ? 'mine' : ''}`}>
                  <div className="th-a">{m.author} · {hubDateTime(m.at)}</div>
                  <p className="th-t">{m.text}</p>
                </div>
              ))}
            </div>
            <div className="th-foot">
              {(t.canSetStatus || canWithdraw) && (
                <div className="th-status">
                  {t.canSetStatus && (
                    <>
                      Status:
                      <select value={status} aria-label="Status" onChange={(e) => setPick(e.target.value)}>
                        {t.statuses.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
                      </select>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busy || status === t.app.status}
                        onClick={async () => {
                          // Saved, only what was sent is cleared: a status picked while it was on its way stays.
                          if (await act(() => sendJson('PATCH', `/api/applications/${enc(appId)}/status`, { status }), `Status → ${pretty(status)}`)) setPick((p) => (p === status ? '' : p));
                        }}
                      >
                        Update
                      </button>
                    </>
                  )}
                  {canWithdraw && (
                    <button
                      type="button"
                      className={`btn btn-sm ${armed ? 'armed' : 'th-withdraw'}`}
                      disabled={busy}
                      onClick={() => (armed ? act(() => sendJson('PATCH', `/api/applications/${enc(appId)}/status`, { status: 'withdrawn' }), 'Withdrawn') : (setArmed(true), setTimeout(() => setArmed(false), 4000)))}
                    >
                      {armed ? 'Click again to withdraw' : 'Withdraw application'}
                    </button>
                  )}
                </div>
              )}
              {msg && <p className="th-note" role="status">{msg}</p>}
              <form
                className="th-reply"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!text.trim() || busy) return;
                  // Keep the text when sending failed, so it can be sent again. Sent, only what was sent is
                  // cleared: a line added while it was on its way stays.
                  if (await act(() => sendJson('POST', `/api/applications/${enc(appId)}/messages`, { text }), 'Sent')) setText((x) => (x === text ? '' : x));
                }}
              >
                <textarea className="field" rows={2} placeholder="Write a message…" aria-label="Message" value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} />
                <button type="submit" className="btn" disabled={busy || !text.trim()}>Send</button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
